/** Cadastro e edição de cliente. Onda 13. */
import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import type { ClientDTO } from "@unik/shared";
import { api, ApiClientError } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";

type Form = Record<string, string>;
const EMPTY: Form = {
  name: "", businessName: "", tier: "mentoria", status: "active",
  whatsapp: "", email: "", city: "", segment: "", positioning: "",
};

export function ClienteForm() {
  const { id } = useParams();
  const editing = id !== undefined;
  const navigate = useNavigate();

  const [form, setForm] = useState<Form>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!editing) return;
    api
      .get<ClientDTO>(`/clients/${id}`)
      .then((c) => {
        setForm({
          name: c.name, businessName: c.businessName ?? "", tier: c.tier, status: c.status,
          whatsapp: c.whatsapp ?? "", email: c.email ?? "", city: c.city ?? "",
          segment: c.segment ?? "", positioning: c.positioning ?? "",
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Cliente não encontrado.");
        setLoading(false);
      });
  }, [editing, id]);

  const set =
    (k: string) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const saved = editing
        ? await api.patch<ClientDTO>(`/clients/${id}`, form)
        : await api.post<ClientDTO>("/clients", form);
      navigate(`/clientes/${saved.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError("Não foi possível salvar.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell>
      <h1 className="type-h1 text-text-cream">{editing ? "Editar cliente" : "Novo cliente"}</h1>

      {loading ? (
        <p className="type-body mt-6 text-text-secondary">Carregando…</p>
      ) : (
        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-5" noValidate>
          <Input label="Nome" value={form.name} onChange={set("name")} error={fieldErrors.name} required />
          <Input label="Empresa / clínica" value={form.businessName} onChange={set("businessName")} />
          <Select label="Plano" value={form.tier} onChange={set("tier")}>
            <option value="mentoria">Mentoria</option>
            <option value="consultoria">Consultoria</option>
            <option value="imersao">Imersão</option>
          </Select>
          <Input
            label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")}
            placeholder="+55 31 9..." hint="Com DDD — usado no link de aprovação."
          />
          <Input label="E-mail" type="email" value={form.email} onChange={set("email")} />
          <Input label="Cidade" value={form.city} onChange={set("city")} />
          <Input
            label="Segmento" value={form.segment} onChange={set("segment")}
            placeholder="Ex.: harmonização facial"
          />
          {editing && (
            <Select label="Status" value={form.status} onChange={set("status")}>
              <option value="active">Ativo</option>
              <option value="suspended">Suspenso</option>
              <option value="archived">Arquivado</option>
            </Select>
          )}
          {error !== null && (
            <p className="type-caption text-danger" role="alert">{error}</p>
          )}
          <div className="flex gap-3">
            <Button type="submit" loading={saving}>{editing ? "Salvar" : "Cadastrar"}</Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(editing ? `/clientes/${id}` : "/clientes")}
            >
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
